/**
 * The analytics product: data sources, pipes, and the endpoints they publish.
 *
 * A managed ClickHouse instance on its own is a database. What makes it a
 * product is this layer: you land events in a **data source**, shape them with
 * a **pipe** — a chain of SQL nodes, each able to read the ones before it — and
 * publish the last node as a **parameterised HTTP endpoint** that answers in
 * milliseconds.
 *
 * The load-bearing decision is where an endpoint is *served*. Not here, and not
 * by the Worker: a published endpoint is answered by the daemon sitting beside
 * ClickHouse, because a request that crossed Cloudflare first would spend more
 * time in the air than in the query. This file is only the vocabulary the
 * control plane, the daemon and the dashboard all have to agree on.
 */

/** ClickHouse column types we let a data source declare. */
export const COLUMN_TYPES = [
  "String",
  "UInt8",
  "UInt16",
  "UInt32",
  "UInt64",
  "Int8",
  "Int16",
  "Int32",
  "Int64",
  "Float32",
  "Float64",
  "Bool",
  "Date",
  "DateTime",
  "DateTime64(3)",
  "UUID",
  "IPv4",
  "IPv6",
] as const;

export type ColumnType = (typeof COLUMN_TYPES)[number];

export const isColumnType = (value: string): value is ColumnType =>
  (COLUMN_TYPES as readonly string[]).includes(value);

export interface Column {
  name: string;
  type: ColumnType;
  /** Nullable columns cannot be part of a sorting key, which is why this is here. */
  nullable?: boolean;
}

/**
 * A table, plus the two decisions that decide whether queries over it are fast.
 *
 * `sortingKey` is the one that matters most and the one people get wrong. It is
 * the physical order of the data, so a filter on its leading columns skips
 * whole granules instead of reading them; a query that filters on something not
 * in the key reads everything. `partitionBy` is coarser — usually a month — and
 * exists so that dropping old data is a file delete rather than a mutation.
 */
export interface DataSource {
  id: string;
  name: string;
  columns: Column[];
  sortingKey: string[];
  /** A ClickHouse expression, e.g. `toYYYYMM(timestamp)`. Empty means unpartitioned. */
  partitionBy: string;
  /** TTL expression, e.g. `timestamp + INTERVAL 90 DAY`. Empty means keep forever. */
  ttl: string;
  rows: number;
  bytes: number;
  createdAt: string;
}

/** One step of a pipe. Each node may select from the data sources and from any earlier node by name. */
export interface PipeNode {
  name: string;
  sql: string;
}

/**
 * A chain of SQL nodes, optionally with one of them published.
 *
 * Nodes exist so a hard query can be written in readable steps and each step
 * inspected on its own; they are inlined into a single statement when the pipe
 * runs, so the chain costs nothing at query time.
 */
export interface Pipe {
  id: string;
  name: string;
  description: string;
  nodes: PipeNode[];
  /** Which node is exposed as an endpoint. Null means the pipe is a draft. */
  endpointNode: string | null;
  /** Parameters found in the published node's SQL, derived rather than declared. */
  params: EndpointParam[];
  /** Seconds a result may be reused for. 0 disables caching entirely. */
  cacheSeconds: number;
  /** ClickHouse threads for this endpoint. 0 leaves the server's own default. */
  maxThreads: number;
  createdAt: string;
  publishedAt: string | null;
}

/* -- parameters ------------------------------------------------------------ */

/**
 * The types a parameter may take. Deliberately a closed set: the type is what
 * makes substitution safe, because a value is rendered according to its type
 * rather than pasted into the SQL. There is no "raw" type, and there will not
 * be one.
 */
export const PARAM_TYPES = [
  "String",
  "Int32",
  "Int64",
  "Float64",
  "Boolean",
  "Date",
  "DateTime",
  "Array(String)",
  "Array(Int64)",
] as const;

export type ParamType = (typeof PARAM_TYPES)[number];

export const isParamType = (value: string): value is ParamType =>
  (PARAM_TYPES as readonly string[]).includes(value);

export interface EndpointParam {
  name: string;
  type: ParamType;
  /** The literal written in the template, if any. Absent makes the param required. */
  defaultValue: string | null;
  required: boolean;
}

/**
 * `{{Type(name)}}` or `{{Type(name, default)}}`.
 *
 * Kept deliberately close to Tinybird's spelling, because anyone arriving with
 * a pipe already written should not have to learn a second dialect for the same
 * idea. The default may be quoted or bare; a quoted one keeps its quotes here
 * and they are stripped when the value is rendered.
 */
const PARAM_RE = /\{\{\s*([A-Za-z0-9_()]+)\s*\(\s*([A-Za-z_][A-Za-z0-9_]*)\s*(?:,\s*([^)]*?)\s*)?\)\s*\}\}/g;

/**
 * Reads the parameters out of a template.
 *
 * Derived from the SQL rather than declared alongside it, so the two can never
 * disagree — the commonest way a published endpoint breaks is a parameter list
 * that has drifted from the query it describes.
 */
export function parseParams(sql: string): EndpointParam[] {
  const found = new Map<string, EndpointParam>();

  for (const match of sql.matchAll(PARAM_RE)) {
    const [, rawType, name, rawDefault] = match;
    if (!rawType || !name || !isParamType(rawType)) continue;

    // First mention wins: a parameter used twice, once with a default and once
    // without, is one parameter and the earlier spelling is the declaration.
    if (found.has(name)) continue;

    const defaultValue = rawDefault === undefined || rawDefault === "" ? null : rawDefault;
    found.set(name, {
      name,
      type: rawType,
      defaultValue,
      required: defaultValue === null,
    });
  }

  return [...found.values()];
}

/** Names the query string uses for its own purposes, which a parameter must not shadow. */
export const RESERVED_PARAMS = new Set(["format", "q", "token", "limit_", "explain"]);

const PARAM_NAME_RE = /^[a-z_][a-z0-9_]*$/i;

export const isValidParamName = (value: string): boolean =>
  PARAM_NAME_RE.test(value) && !RESERVED_PARAMS.has(value.toLowerCase());

/* -- tokens ---------------------------------------------------------------- */

/**
 * What a token may do. Scopes are per-resource on purpose: the token you put in
 * a browser to read one endpoint should not also be able to append to a data
 * source or read a different endpoint.
 */
export type ScopeType = "read" | "append" | "admin";

export interface TokenScope {
  type: ScopeType;
  /** The pipe or data source this applies to. Null on `admin`, which is workspace-wide. */
  resource: string | null;
}

export interface ApiToken {
  id: string;
  name: string;
  scopes: TokenScope[];
  createdAt: string;
  lastUsedAt: string | null;
}

/** Handed over once, at creation. Only the hash is kept afterwards. */
export interface ApiTokenSecret extends ApiToken {
  token: string;
}

export const describeScope = (scope: TokenScope): string =>
  scope.type === "admin" ? "admin (whole workspace)" : `${scope.type} ${scope.resource ?? "*"}`;

/* -- what an endpoint answers ---------------------------------------------- */

export interface ResultColumn {
  name: string;
  type: string;
}

/**
 * The shape a published endpoint returns.
 *
 * `statistics` is not decoration. It is how someone finds out that an endpoint
 * got slow because it started scanning the whole table, which is the single
 * most common way a fast endpoint becomes a slow one, and it is invisible
 * without the row and byte counts.
 */
export interface QueryResult {
  meta: ResultColumn[];
  data: Record<string, unknown>[];
  rows: number;
  statistics: {
    /** Seconds, as ClickHouse reports it. */
    elapsed: number;
    rows_read: number;
    bytes_read: number;
  };
}

/* -- observability --------------------------------------------------------- */

/**
 * Rolling latency for one endpoint, measured by the daemon that serves it.
 *
 * Measured where the request is answered rather than where it is managed: the
 * Worker never sees these requests, so a number collected there would be
 * measuring something else entirely.
 */
export interface EndpointStats {
  pipe: string;
  requests: number;
  errors: number;
  /** Milliseconds, server-side, from accept to last byte written. */
  p50: number;
  p95: number;
  p99: number;
  rowsRead: number;
  bytesRead: number;
  /** ISO-8601 start of the window these cover. */
  since: string;
}

/* -- naming ---------------------------------------------------------------- */

/**
 * Data source and pipe names become ClickHouse identifiers *and* path segments
 * in a published URL, so they are narrower than either would require alone.
 */
const RESOURCE_RE = /^[a-z][a-z0-9_]{0,62}$/;

/** Names ClickHouse or the API surface already owns. */
const RESERVED_RESOURCES = new Set([
  "all",
  "default",
  "events",
  "information_schema",
  "pipes",
  "query",
  "saucer",
  "sql",
  "system",
  "tokens",
  "v0",
]);

export const isValidResourceName = (value: string): boolean =>
  RESOURCE_RE.test(value) && !RESERVED_RESOURCES.has(value);

/* -- table expressions ----------------------------------------------------- */

/**
 * `PARTITION BY` and `TTL` are the two places a data source definition lets
 * free-form SQL through, and both land at the end of a `CREATE TABLE`. Left
 * unchecked, either one can close the statement and continue it — a `TTL` of
 * `d + INTERVAL 1 DAY` followed by anything at all is appended verbatim.
 *
 * So they are validated against a deliberately small grammar rather than
 * sanitised. Sanitising means guessing at what is dangerous; an allowlist means
 * only shapes we understand get through, and the shapes people actually use
 * here are few. Anything rejected can still be had by asking.
 *
 * The functions permitted are the ones that make sense for a partition key or a
 * retention rule, and nothing that reads data: no subqueries, no `dictGet`, no
 * table functions.
 */
const TABLE_EXPR_FUNCTIONS = new Set([
  "toyyyymm",
  "toyyyymmdd",
  "todate",
  "todatetime",
  "tostartofday",
  "tostartofweek",
  "tostartofmonth",
  "tostartofhour",
  "toyear",
  "tomonth",
  "intdiv",
  "cityhash64",
  "modulo",
  "tuple",
]);

const INTERVAL_UNITS = new Set([
  "second",
  "minute",
  "hour",
  "day",
  "week",
  "month",
  "quarter",
  "year",
]);

/**
 * Tokenises into identifiers, numbers, and the few operators these expressions
 * need. Anything else — a quote, a semicolon, a comment marker, a bracket —
 * fails the whole expression rather than being stripped.
 */
function tokenizeTableExpr(value: string): string[] | null {
  const tokens: string[] = [];
  let index = 0;

  while (index < value.length) {
    const char = value[index]!;

    if (/\s/.test(char)) {
      index += 1;
      continue;
    }
    if (/[A-Za-z_]/.test(char)) {
      let end = index;
      while (end < value.length && /[A-Za-z0-9_]/.test(value[end]!)) end += 1;
      tokens.push(value.slice(index, end));
      index = end;
      continue;
    }
    if (/[0-9]/.test(char)) {
      let end = index;
      while (end < value.length && /[0-9]/.test(value[end]!)) end += 1;
      tokens.push(value.slice(index, end));
      index = end;
      continue;
    }
    if ("(),+-*/%".includes(char)) {
      tokens.push(char);
      index += 1;
      continue;
    }
    // A quote, semicolon, dot, dash-dash comment, anything else: refuse.
    return null;
  }

  return tokens;
}

/**
 * Whether an expression is one of the shapes we allow, given the columns that
 * exist. Every bare identifier must be a column, a permitted function, or an
 * interval unit — which is what stops `other_db.secrets` and `dictGet` alike.
 */
export function isValidTableExpression(value: string, columns: string[]): boolean {
  const trimmed = value.trim();
  if (trimmed === "") return true;
  if (trimmed.length > 200) return false;

  const tokens = tokenizeTableExpr(trimmed);
  if (!tokens) return false;

  const known = new Set(columns.map((column) => column.toLowerCase()));
  let depth = 0;

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index]!;

    if (token === "(") depth += 1;
    else if (token === ")") depth -= 1;
    if (depth < 0) return false;

    if (!/^[A-Za-z_]/.test(token)) continue;

    const lowered = token.toLowerCase();
    if (lowered === "interval") continue;
    if (INTERVAL_UNITS.has(lowered)) continue;
    if (known.has(lowered)) continue;

    // A bare word left over is only acceptable as a function, and only if it
    // is actually being called.
    if (TABLE_EXPR_FUNCTIONS.has(lowered) && tokens[index + 1] === "(") continue;

    return false;
  }

  return depth === 0;
}

/** The URL a published pipe answers on. */
export const endpointUrl = (apiHost: string, pipe: string): string =>
  `https://${apiHost}/v0/pipes/${pipe}.json`;

/** The ingest URL for a data source. */
export const eventsUrl = (apiHost: string, datasource: string): string =>
  `https://${apiHost}/v0/events?name=${datasource}`;

/** `api.saucer.sh` — the hostname the daemon answers the product on. */
export const apiHostFor = (root: string): string => `api.${root}`;

/**
 * A ready-to-run example call, used by the dashboard and the docs so the thing
 * someone copies is the thing that works.
 */
export function endpointExample(apiHost: string, pipe: string, params: EndpointParam[]): string {
  const query = params
    .map((param) => `${param.name}=${param.defaultValue ? stripQuotes(param.defaultValue) : "…"}`)
    .join("&");
  const url = `${endpointUrl(apiHost, pipe)}${query ? `?${query}` : ""}`;
  return `curl "${url}" -H "Authorization: Bearer $TOKEN"`;
}

const stripQuotes = (value: string): string => value.replace(/^['"]|['"]$/g, "");
